/**
 * 专门管理聊天记录的 IndexedDB
 * 用于持久化数字人、会话和消息数据
 */

import { useChatStore } from '@/stores/chat'

export interface ChatRecordDigitalHuman {
  id: string
  name: string
  avatar: string
  currentSessionId: string | null
  lastActiveTime: number
  createdAt: number
  updatedAt: number
}

export interface ChatRecordSession {
  id: string
  digitalHumanId: string
  name: string
  createdAt: number
  updatedAt: number
  avatar?: string
  lastMessage?: string
}

export interface ChatRecordMessage {
  id: string
  sessionId: string
  content: string
  type: string // 'user' | 'ai' | 'system'
  status: string // 'sending' | 'sent' | 'received' | 'failed'
  timestamp: number
  username?: string
  avatar?: string
  msgId?: number | string
  // 翻译相关
  translated?: boolean
  translatedContent?: string
  translatedLang?: string
  globalTranslation?: boolean // 🔧 新增：是否在全局翻译开启后的消息
  // 生图相关
  image?: string
  hasGeneratedImage?: boolean
  isGeneratingImage?: boolean
  isSuccessful?: boolean // 🔧 新增：生图是否成功（true=成功, false=失败, undefined=未生图）
  imageProgress?: number
  video?: string
  coverImage?: string
  // 其他
  isTyping?: boolean
  isLoadingTranslate?: boolean
}

class ChatRecordsDB {
  private db: IDBDatabase | null = null
  private readonly dbName = 'ChatRecordsDB'
  private readonly version = 2

  /**
   * 初始化数据库
   */
  async init(): Promise<void> {
    if (this.db) {
      return
    }

    return new Promise<void>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version)

      req.onerror = () => {
        console.error('❌ ChatRecordsDB 打开失败:', req.error)
        reject(req.error)
      }

      req.onsuccess = () => {
        this.db = req.result
        resolve()
      }

      req.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result

        // 数字人表
        if (!db.objectStoreNames.contains('digitalHumans')) {
          const dhStore = db.createObjectStore('digitalHumans', { keyPath: 'id' })
          dhStore.createIndex('lastActiveTime', 'lastActiveTime', { unique: false })
          dhStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          console.log('📦 创建数字人表')
        }

        // 会话表
        if (!db.objectStoreNames.contains('sessions')) {
          const sessionStore = db.createObjectStore('sessions', { keyPath: 'id' })
          sessionStore.createIndex('digitalHumanId', 'digitalHumanId', { unique: false })
          sessionStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          console.log('📦 创建会话表')
        }

        // 消息表
        if (!db.objectStoreNames.contains('messages')) {
          const msgStore = db.createObjectStore('messages', { keyPath: 'id' })
          msgStore.createIndex('sessionId', 'sessionId', { unique: false })
          msgStore.createIndex('timestamp', 'timestamp', { unique: false })
          msgStore.createIndex('msgId', 'msgId', { unique: false })
          msgStore.createIndex('type', 'type', { unique: false })
          console.log('📦 创建消息表')
        }
      }
    })
  }

  /**
   * 获取事务
   */
  private getTransaction(storeNames: string[], mode: IDBTransactionMode): IDBTransaction {
    if (!this.db) throw new Error('ChatRecordsDB 未初始化，请先调用 init()')
    return this.db.transaction(storeNames, mode)
  }

  /**
   * Promise 化 IDBRequest
   */
  private promisify<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result as T)
      req.onerror = () => reject(req.error)
    })
  }

  // ==================== 数字人操作 ====================

  /**
   * 保存数字人
   */
  async saveDigitalHuman(dh: ChatRecordDigitalHuman): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['digitalHumans'], 'readwrite')
    await this.promisify(tx.objectStore('digitalHumans').put(dh))
  }

  /**
   * 批量保存数字人
   */
  async saveDigitalHumans(dhs: ChatRecordDigitalHuman[]): Promise<void> {
    if (dhs.length === 0) return
    await this.init()
    const tx = this.getTransaction(['digitalHumans'], 'readwrite')
    const store = tx.objectStore('digitalHumans')
    await Promise.all(dhs.map(dh => this.promisify(store.put(dh))))
  }

  /**
   * 获取数字人
   */
  async getDigitalHuman(id: string): Promise<ChatRecordDigitalHuman | null> {
    await this.init()
    const tx = this.getTransaction(['digitalHumans'], 'readonly')
    const result = await this.promisify<ChatRecordDigitalHuman | undefined>(
      tx.objectStore('digitalHumans').get(id)
    )
    return result || null
  }

  /**
   * 获取所有数字人
   */
  async getAllDigitalHumans(): Promise<ChatRecordDigitalHuman[]> {
    await this.init()
    const tx = this.getTransaction(['digitalHumans'], 'readonly')
    return this.promisify<ChatRecordDigitalHuman[]>(tx.objectStore('digitalHumans').getAll())
  }

  /**
   * 删除数字人
   */
  async deleteDigitalHuman(id: string): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['digitalHumans'], 'readwrite')
    await this.promisify(tx.objectStore('digitalHumans').delete(id))
  }

  // ==================== 会话操作 ====================

  /**
   * 保存会话
   */
  async saveSession(session: ChatRecordSession): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['sessions'], 'readwrite')
    await this.promisify(tx.objectStore('sessions').put(session))
  }

  /**
   * 批量保存会话
   */
  async saveSessions(sessions: ChatRecordSession[]): Promise<void> {
    if (sessions.length === 0) return
    await this.init()
    const tx = this.getTransaction(['sessions'], 'readwrite')
    const store = tx.objectStore('sessions')
    await Promise.all(sessions.map(s => this.promisify(store.put(s))))
  }

  /**
   * 获取会话
   */
  async getSession(id: string): Promise<ChatRecordSession | null> {
    await this.init()
    const tx = this.getTransaction(['sessions'], 'readonly')
    const result = await this.promisify<ChatRecordSession | undefined>(
      tx.objectStore('sessions').get(id)
    )
    return result || null
  }

  /**
   * 根据数字人ID获取所有会话
   */
  async getSessionsByDigitalHuman(digitalHumanId: string): Promise<ChatRecordSession[]> {
    await this.init()
    const tx = this.getTransaction(['sessions'], 'readonly')
    const index = tx.objectStore('sessions').index('digitalHumanId')
    const sessions = await this.promisify<ChatRecordSession[]>(index.getAll(digitalHumanId))
    // 按更新时间倒序排序
    return sessions.sort((a, b) => b.updatedAt - a.updatedAt)
  }

  /**
   * 删除会话
   */
  async deleteSession(id: string): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['sessions'], 'readwrite')
    await this.promisify(tx.objectStore('sessions').delete(id))
  }

  /**
   * 删除数字人的所有会话
   */
  async deleteSessionsByDigitalHuman(digitalHumanId: string): Promise<void> {
    await this.init()
    const sessions = await this.getSessionsByDigitalHuman(digitalHumanId)
    await Promise.all(sessions.map(s => this.deleteSession(s.id)))
  }

  // ==================== 消息操作 ====================

  /**
   * 保存消息
   */
  async saveMessage(message: ChatRecordMessage): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['messages'], 'readwrite')
    await this.promisify(tx.objectStore('messages').put(message))
  }

  /**
   * 批量保存消息
   */
  async saveMessages(messages: ChatRecordMessage[]): Promise<void> {
    if (messages.length === 0) return
    await this.init()
    const tx = this.getTransaction(['messages'], 'readwrite')
    const store = tx.objectStore('messages')
    await Promise.all(messages.map(m => this.promisify(store.put(m))))
  }

  /**
   * 获取消息
   */
  async getMessage(id: string): Promise<ChatRecordMessage | null> {
    await this.init()
    const tx = this.getTransaction(['messages'], 'readonly')
    const result = await this.promisify<ChatRecordMessage | undefined>(
      tx.objectStore('messages').get(id)
    )
    return result || null
  }

  /**
   * 根据会话ID获取所有消息
   */
  async getMessagesBySession(sessionId: string): Promise<ChatRecordMessage[]> {
    await this.init()
    const tx = this.getTransaction(['messages'], 'readonly')
    const index = tx.objectStore('messages').index('sessionId')
    const messages = await this.promisify<ChatRecordMessage[]>(index.getAll(sessionId))
    // 按时间戳正序排序
    return messages.sort((a, b) => a.timestamp - b.timestamp)
  }

  /**
   * 根据数字人ID获取所有消息（通过会话）
   */
  async getMessagesByDigitalHuman(digitalHumanId: string): Promise<ChatRecordMessage[]> {
    await this.init()
    const sessions = await this.getSessionsByDigitalHuman(digitalHumanId)
    const allMessages: ChatRecordMessage[] = []
    for (const session of sessions) {
      const messages = await this.getMessagesBySession(session.id)
      allMessages.push(...messages)
    }
    return allMessages.sort((a, b) => a.timestamp - b.timestamp)
  }

  /**
   * 更新消息
   */
  async updateMessage(id: string, updates: Partial<ChatRecordMessage>): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['messages'], 'readwrite')
    const store = tx.objectStore('messages')
    const existing = await this.promisify<ChatRecordMessage | undefined>(store.get(id))
    if (!existing) {
      console.warn(`⚠️ 消息 ${id} 不存在，无法更新`)
      return
    }
    const updated = { ...existing, ...updates }
    await this.promisify(store.put(updated))
  }

  /**
   * 删除消息
   */
  async deleteMessage(id: string): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['messages'], 'readwrite')
    await this.promisify(tx.objectStore('messages').delete(id))
  }

  /**
   * 删除会话的所有消息
   */
  async deleteMessagesBySession(sessionId: string): Promise<void> {
    await this.init()
    const messages = await this.getMessagesBySession(sessionId)
    await Promise.all(messages.map(m => this.deleteMessage(m.id)))
  }

  /**
   * 定期清理聊天记录
   * 每天清理一次，每个角色只保留最新10条消息
   */
  async cleanupChatRecords(): Promise<void> {
    try {
      const LAST_CLEANUP_KEY = 'last_chat_cleanup_time'
      // const CLEANUP_INTERVAL = 12 * 60 * 60 * 1000 // 1天（毫秒）
      const MESSAGES_TO_KEEP = 20 // 每个角色保留的消息数量

      // 获取上次清理时间，避免频繁执行
      // const lastCleanupTime = localStorage.getItem(LAST_CLEANUP_KEY)
      const now = Date.now()
      // if (lastCleanupTime && now - parseInt(lastCleanupTime) < CLEANUP_INTERVAL) {
      //   console.log('📅 距离上次清理不足24小时，跳过清理')
      //   return
      // }

      console.log('🧹 开始清理聊天记录：每个角色保留最新10条消息...')

      // 初始化数据库
      await chatRecordsDB.init()

      // 获取所有数字人
      const allDigitalHumans = await chatRecordsDB.getAllDigitalHumans()
      let totalDeletedCount = 0

      const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000
      for (const dh of allDigitalHumans) {
        console.log(`🔄 清理数字人 ${dh.name} (${dh.id}) 的消息...`)

        // 检查该数字人最近一次活跃时间或最近消息时间
        const allMessagesForDh = await chatRecordsDB.getMessagesByDigitalHuman(dh.id)
        let latestTimestamp = dh.lastActiveTime || 0
        if (allMessagesForDh.length > 0) {
          // getMessagesByDigitalHuman 返回按时间正序排序，最后一条为最新
          latestTimestamp = Math.max(latestTimestamp, allMessagesForDh[allMessagesForDh.length - 1].timestamp)
        }

        // 如果超过两天未活跃，清空该数字人的全部本地数据（数字人、会话、消息）
        if (latestTimestamp > 0 && now - latestTimestamp > TWO_DAYS_MS) {
          console.log(`🗑️ 数字人 ${dh.name}(${dh.id}) 超过两天未活跃，删除全部本地数据...`)
          // 记录将被删除的消息数（用于统计）
          const messagesCount = allMessagesForDh.length
          await chatRecordsDB.deleteDigitalHumanData(dh.id)
          totalDeletedCount += messagesCount
          continue
        }

        // 否则，按照原有规则只保留每个会话的最新 MESSAGES_TO_KEEP 条消息
        // 获取该数字人的所有会话
        const sessions = await chatRecordsDB.getSessionsByDigitalHuman(dh.id)
        let digitalHumanDeletedCount = 0

        for (const session of sessions) {
          // 获取该会话的所有消息
          const messages = await chatRecordsDB.getMessagesBySession(session.id)

          if (messages.length > MESSAGES_TO_KEEP) {
            // 按时间戳排序（最新的在前）
            messages.sort((a, b) => b.timestamp - a.timestamp)

            // 保留最新的10条，删除其余的
            const messagesToDelete = messages.slice(MESSAGES_TO_KEEP)

            console.log(`📝 会话 ${session.id} 有 ${messages.length} 条消息，删除 ${messagesToDelete.length} 条旧消息`)

            // 批量删除旧消息
            for (const message of messagesToDelete) {
              await chatRecordsDB.deleteMessage(message.id)
              digitalHumanDeletedCount++
            }
          }
        }

        if (digitalHumanDeletedCount > 0) {
          console.log(`✅ 数字人 ${dh.name} 删除了 ${digitalHumanDeletedCount} 条旧消息`)
          totalDeletedCount += digitalHumanDeletedCount
        } else {
          console.log(`ℹ️ 数字人 ${dh.name} 消息数量正常，无需清理`)
        }
      }

      // 同步清除内存中的消息（如 store 可用）
      try {
        const chatStore = useChatStore()
        if (chatStore && typeof chatStore.deleteAllDigitalHumanMessages === 'function') {
          // 这里我们不再删除所有消息，而是让store根据保留的消息数量来处理
          chatStore.deleteAllDigitalHumanMessages()
          // 可以通过重新加载数据来同步
          console.log('🔄 建议刷新页面以同步内存中的消息数据')
        }
      } catch (e) {
        // 如果在此上下文无法访问 Pinia store，忽略并继续
        console.warn('无法访问 chat store，同步内存清理跳过:', e)
      }

      // 更新清理时间
      localStorage.setItem(LAST_CLEANUP_KEY, now.toString())

      if (totalDeletedCount > 0) {
        console.log(`✅ 清理完成，共删除了 ${totalDeletedCount} 条过期消息`)
      } else {
        console.log('✅ 清理完成，所有数字人的消息数量都在正常范围内')
      }
    } catch (error) {
      console.error('❌ 清理聊天记录失败:', error)
    }
  }

  // ==================== 批量操作 ====================

  /**
   * 获取完整的数字人数据（包含会话和消息）
   */
  async getDigitalHumanWithData(id: string): Promise<{
    digitalHuman: ChatRecordDigitalHuman | null
    sessions: ChatRecordSession[]
    messages: Record<string, ChatRecordMessage[]> // sessionId -> messages
  }> {
    await this.init()
    const digitalHuman = await this.getDigitalHuman(id)
    if (!digitalHuman) {
      return { digitalHuman: null, sessions: [], messages: {} }
    }

    const sessions = await this.getSessionsByDigitalHuman(id)
    const messages: Record<string, ChatRecordMessage[]> = {}

    for (const session of sessions) {
      messages[session.id] = await this.getMessagesBySession(session.id)
    }

    return { digitalHuman, sessions, messages }
  }

  /**
   * 清空所有数据
   */
  async clearAll(): Promise<void> {
    await this.init()
    const tx = this.getTransaction(['digitalHumans', 'sessions', 'messages'], 'readwrite')
    await Promise.all([
      this.promisify(tx.objectStore('digitalHumans').clear()),
      this.promisify(tx.objectStore('sessions').clear()),
      this.promisify(tx.objectStore('messages').clear())
    ])
    console.log('🗑️ 已清空所有聊天记录数据')
  }

  /**
   * 删除数字人的所有数据（数字人、会话、消息）
   */
  async deleteDigitalHumanData(id: string): Promise<void> {
    await this.init()
    // 先删除消息
    const sessions = await this.getSessionsByDigitalHuman(id)
    for (const session of sessions) {
      await this.deleteMessagesBySession(session.id)
    }
    // 再删除会话
    await this.deleteSessionsByDigitalHuman(id)
    // 最后删除数字人
    await this.deleteDigitalHuman(id)
    console.log(`🗑️ 已删除数字人 ${id} 的所有数据`)
  }
}

// 导出单例
export const chatRecordsDB = new ChatRecordsDB()
